import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { getCurrentUserPreferences, updateCurrentUserPreferences } from 'api/userPreferences';
import { useAppSelector } from 'redux/hooks';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { shouldRestoreNavigationPreferencesOnRoute } from './navigationPreferences';
import { useCaseIdToLabId } from './useCaseIdToLabId';
import {
  buildLastLabPreferencesPatch,
  getLastLabIdFromUserPreferences,
  getStoredLastLabId,
  setStoredLastLabId,
} from './userPreferences';

const PATHS_WITH_LAB_PREFERENCE = ['/', '/procedures', '/uploads-dashboard', '/lost-and-found'];

// Sync lab with case's lab when in case page
export const useSetInitialLabId = () => {
  const { accessToken, triedAuthenticateFromLocalStorage } = useAppSelector((state) => state.auth);
  const { queryLabId, setLabId, userLabId } = useCurrentLabId();
  const { procedureLabId, caseId } = useCaseIdToLabId();
  const location = useLocation();
  const loadedAccessTokenRef = useRef<string | null>(null);
  const persistedLabIdRef = useRef<string | null>(null);
  const appliedStoredLoginLabRef = useRef(false);

  const hasCaseId = Boolean(caseId) && !isNaN(caseId);
  const canPersistLabPreference = PATHS_WITH_LAB_PREFERENCE.includes(location.pathname);
  const canRestoreLoginLabPreference = canPersistLabPreference && shouldRestoreNavigationPreferencesOnRoute(location);

  useEffect(() => {
    if (!queryLabId && hasCaseId && procedureLabId) {
      // Sync lab with case's lab when in case page
      setLabId(procedureLabId, 'replaceIn');
    } else if (!queryLabId && !hasCaseId && canRestoreLoginLabPreference && !appliedStoredLoginLabRef.current) {
      const storedLabId = getStoredLastLabId();

      if (storedLabId) {
        setLabId(storedLabId, 'replaceIn');
      } else if (userLabId) {
        setLabId(userLabId, 'replaceIn');
      }

      appliedStoredLoginLabRef.current = true;
    }
  }, [canRestoreLoginLabPreference, userLabId, hasCaseId, queryLabId, setLabId, procedureLabId]);

  useEffect(() => {
    if (!triedAuthenticateFromLocalStorage || !canRestoreLoginLabPreference) {
      return;
    }

    if (!accessToken) {
      loadedAccessTokenRef.current = null;
      persistedLabIdRef.current = null;
      return;
    }

    if (loadedAccessTokenRef.current === accessToken) {
      return;
    }

    loadedAccessTokenRef.current = accessToken;

    let ignoreResponse = false;

    void getCurrentUserPreferences()
      .then((preferences) => {
        if (ignoreResponse) {
          return;
        }

        const serverLastLabId = getLastLabIdFromUserPreferences(preferences ?? {});
        if (serverLastLabId) {
          setStoredLastLabId(serverLastLabId);

          if (!queryLabId && !hasCaseId) {
            setLabId(serverLastLabId, 'replaceIn');
          }

          return;
        }

        const storedLabId = getStoredLastLabId();
        if (storedLabId) {
          void updateCurrentUserPreferences(buildLastLabPreferencesPatch(storedLabId)).catch(() => undefined);
        }
      })
      .catch(() => undefined);

    return () => {
      ignoreResponse = true;
    };
  }, [accessToken, canRestoreLoginLabPreference, hasCaseId, queryLabId, setLabId, triedAuthenticateFromLocalStorage]);

  useEffect(() => {
    if (!queryLabId || hasCaseId || !canPersistLabPreference) {
      return;
    }

    setStoredLastLabId(queryLabId);

    if (!accessToken || persistedLabIdRef.current === queryLabId) {
      return;
    }

    persistedLabIdRef.current = queryLabId;
    void updateCurrentUserPreferences(buildLastLabPreferencesPatch(queryLabId)).catch(() => undefined);
  }, [accessToken, canPersistLabPreference, hasCaseId, queryLabId]);
};
